/**
 * GroupTable Component
 *
 * Displays a table of groups with actions (view, edit, delete).
 * Shows group name, description, member count, and timestamps.
 */

import React from 'react';
import { UserGroupIcon, EyeIcon, UsersIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { GroupSanitized, GroupListItem } from '../../../types/group.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface GroupTableProps {
  groups: GroupSanitized[];
  isLoading: boolean;
  onViewGroup: (group: GroupSanitized) => void;
  onEditGroup: (group: GroupSanitized) => void;
  onDeleteGroup: (group: GroupSanitized) => void;
  onManageMembers: (group: GroupSanitized) => void;
  /** Permission to edit groups */
  canEdit?: boolean;
  /** Permission to delete groups */
  canDelete?: boolean;
}

export const GroupTable: React.FC<GroupTableProps> = ({
  groups,
  isLoading,
  onViewGroup,
  onEditGroup,
  onDeleteGroup,
  onManageMembers,
  canEdit,
  canDelete,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (groups.length === 0) {
    return (
      <EmptyState
        icon={<UserGroupIcon className="mx-auto h-12 w-12" />}
        title="No groups found"
        description="Get started by creating a new group to organize your users."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Group Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Code
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Description
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Members
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {groups.map((group) => (
            <tr key={group.groupId} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <UserGroupIcon className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {group.groupName}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <CopyableCode value={group.groupCode} />
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {group.description || (
                    <span className="text-gray-400 italic">No description</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex gap-1 flex-wrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {(group as unknown as GroupListItem).userCount ?? 0} {(group as unknown as GroupListItem).userCount === 1 ? 'member' : 'members'}
                  </span>
                  {group.roles && group.roles.length > 0 && (
                    <Badge variant="success">{group.roles.length} role{group.roles.length !== 1 ? 's' : ''}</Badge>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatRelativeTime(group.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onViewGroup(group)}
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onManageMembers(group)}
                    title="Manage Members"
                  >
                    <UsersIcon className="h-4 w-4" />
                  </Button>
                  {canEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEditGroup(group)}
                      title="Edit Group"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDeleteGroup(group)}
                      title="Delete Group"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
